import type CarrierAccount from '~/types/CarrierAccount';

import { ActionKeys } from '~/actions/constants';
import { type ActionTypes } from '~/actions/index';
import { type RegStatus } from '~/types/CarrierAccountRegStatus';
import {
  extractRefreshableLastValue,
  getNotRequested,
  getRefreshFailed,
  getRefreshSuccess,
  getRefreshing,
} from '~/utils/fetchable';

export type CarrierAccountRegStatusMap = { [carrierAccountObjectId: string]: RegStatus };
export type CarrierModalStatus =
  | 'amazon_shipping-registration'
  | 'canada_post-registration'
  | 'closed'
  | 'dhl_express-registration'
  | 'fedex-validation'
  | 'royal_mail_sf-registration'
  | 'slide'
  | 'standalone'
  | 'ups-registration';
export type CarrierAccountsState = {
  readonly carrierAccountRegStatusMap: Refreshable<CarrierAccountRegStatusMap, string>;
  readonly carrierAccounts: readonly Readonly<CarrierAccount>[];
  readonly editingCarrierAccountId: Maybe<string>;
  readonly editingCarrierServiceLevelsError: string[];
  readonly errorMessage: string;
  readonly isFetchingCarrierAccounts: boolean;
  readonly isSavingCarrierAccount: boolean;
  readonly modalStatus: CarrierModalStatus;
  readonly successfulFetch: boolean;
  readonly successfulSubmit: Maybe<CarrierAccount>;
  readonly userAction?: 'connect' | 'edit' | 'none';
};

const initialState: CarrierAccountsState = {
  carrierAccountRegStatusMap: getNotRequested(),
  carrierAccounts: [],
  editingCarrierAccountId: null,
  editingCarrierServiceLevelsError: [],
  errorMessage: '',
  isFetchingCarrierAccounts: false,
  isSavingCarrierAccount: false,
  modalStatus: 'closed',
  successfulFetch: false,
  successfulSubmit: null,
  userAction: 'none',
};

const carrierAccountsReducer = (
  state: CarrierAccountsState = initialState,
  action: ActionTypes,
): CarrierAccountsState => {
  switch (action.type) {
    case ActionKeys.CARRIER_ACCOUNTS_CLEAR_STATE:
      return {
        ...state,
        errorMessage: '',
      };
    case ActionKeys.CARRIER_ACCOUNTS_CLEAR_SUBMIT:
      return {
        ...state,
        successfulSubmit: null,
      };
    case ActionKeys.FETCH_CARRIER_ACCOUNTS:
      return {
        ...state,
        isFetchingCarrierAccounts: true,
      };
    case ActionKeys.FETCH_CARRIER_ACCOUNTS_SUCCESS:
      return {
        ...state,
        carrierAccounts: action.payload.carrierAccounts,
        isFetchingCarrierAccounts: false,
        successfulFetch: true,
      };
    case ActionKeys.FETCH_CARRIER_ACCOUNTS_ERROR:
      return {
        ...state,
        errorMessage: action.payload.error,
        isFetchingCarrierAccounts: false,
      };
    case ActionKeys.FETCH_CARRIER_ACCOUNT_REG_STATUSES:
      return {
        ...state,
        carrierAccountRegStatusMap: getRefreshing(state.carrierAccountRegStatusMap),
      };
    case ActionKeys.FETCH_CARRIER_ACCOUNT_REG_STATUSES_SUCCESS:
      const fetchedStatusMap: CarrierAccountRegStatusMap =
        action.payload.carrierAccountRegStatuses.reduce((mapResult, regStatus) => {
          mapResult[regStatus.carrierAccount] = regStatus.status;
          return mapResult;
        }, {});
      const previouslyFetchedStatuses = extractRefreshableLastValue(
        state.carrierAccountRegStatusMap,
      );
      return {
        ...state,
        carrierAccountRegStatusMap: getRefreshSuccess(state.carrierAccountRegStatusMap, {
          ...previouslyFetchedStatuses,
          ...fetchedStatusMap,
        }),
      };
    case ActionKeys.FETCH_CARRIER_ACCOUNT_REG_STATUSES_ERROR:
      return {
        ...state,
        carrierAccountRegStatusMap: getRefreshFailed(
          state.carrierAccountRegStatusMap,
          action.payload.error,
        ),
      };
    case ActionKeys.CONNECT_CARRIER_ACCOUNT:
      return {
        ...state,
        editingCarrierAccountId: null,
        isSavingCarrierAccount: true,
      };
    case ActionKeys.CONNECT_CARRIER_ACCOUNT_SUCCESS:
      const newCarrierAccount = action.payload.carrierAccount;
      return {
        ...state,
        carrierAccounts: [newCarrierAccount, ...state.carrierAccounts],
        isSavingCarrierAccount: false,
        modalStatus: 'closed',
        successfulSubmit: newCarrierAccount,
        userAction: 'connect',
      };
    case ActionKeys.CONNECT_CARRIER_ACCOUNT_ERROR:
      return {
        ...state,
        errorMessage: action.payload.error,
        isSavingCarrierAccount: false,
        successfulSubmit: null,
        userAction: 'none',
      };
    case ActionKeys.EDIT_CARRIER_ACCOUNT:
      return {
        ...state,
        editingCarrierAccountId: action.payload.id,
        isSavingCarrierAccount: true,
      };
    case ActionKeys.EDIT_CARRIER_ACCOUNT_SUCCESS:
      const updatedCarrierAccount = action.payload.carrierAccount;

      // Update state's carrier accounts array with the updated carrier account
      const carrierAccounts = state.carrierAccounts.map((account: CarrierAccount) => {
        if (account.objectId === updatedCarrierAccount.objectId) return updatedCarrierAccount;

        return account;
      });

      return {
        ...state,
        carrierAccounts,
        isSavingCarrierAccount: false,
        modalStatus: 'closed',
        successfulSubmit: updatedCarrierAccount,
        userAction: 'edit',
      };
    case ActionKeys.EDIT_CARRIER_ACCOUNT_ERROR:
      return {
        ...state,
        errorMessage: action.payload.error,
        isSavingCarrierAccount: false,
        successfulSubmit: null,
        userAction: 'none',
      };
    case ActionKeys.EDIT_CARRIER_ACCOUNT_SERVICE_LEVELS_ERROR:
      return {
        ...state,
        editingCarrierServiceLevelsError: action.payload,
      };
    case ActionKeys.CREATE_CARRIER_ACCOUNT:
      return {
        ...state,
        editingCarrierAccountId: null,
        isSavingCarrierAccount: true,
      };
    case ActionKeys.CREATE_CARRIER_ACCOUNT_SUCCESS:
      const createdCarrierAccount = action.payload.carrierAccount;

      // If an existing carrier was activated, treat this as an "edit" and update the existing state's carrier accounts
      let updatedAccount = false;

      const totalCarrierAccounts = state.carrierAccounts.map((account: CarrierAccount) => {
        if (account.objectId === createdCarrierAccount.objectId) {
          updatedAccount = true;
          return createdCarrierAccount;
        }
        return account;
      });

      return {
        ...state,
        carrierAccounts: updatedAccount
          ? totalCarrierAccounts
          : [createdCarrierAccount, ...state.carrierAccounts],
        isSavingCarrierAccount: false,
        modalStatus: 'closed',
        successfulSubmit: action.payload.carrierAccount,
      };
    case ActionKeys.CREATE_CARRIER_ACCOUNT_ERROR:
      return {
        ...state,
        errorMessage: action.payload.error,
        isSavingCarrierAccount: false,
        successfulSubmit: null,
      };
    case ActionKeys.TOGGLE_CARRIER_MODAL:
      return {
        ...state,
        editingCarrierAccountId:
          action.payload.modalStatus !== 'closed' ? null : state.editingCarrierAccountId,
        editingCarrierServiceLevelsError:
          action.payload.modalStatus !== 'closed' ? [] : state.editingCarrierServiceLevelsError,
        // Clear the following if we are opening modal
        errorMessage: action.payload.modalStatus !== 'closed' ? '' : state.errorMessage,
        modalStatus: action.payload.modalStatus,
        successfulSubmit: action.payload.modalStatus !== 'closed' ? null : state.successfulSubmit,
      };
    default:
      return state;
  }
};

export default carrierAccountsReducer;
